// 2FA rework (2026-08-17) — TanStack Query hooks for the 2FA surface.
//
// Backend endpoints (all Story 1.13, shapes verified against
// server/src/routes/{auth,account}.rs):
//   * POST /api/v1/auth/2fa-verify        { code } → 204. Works on the
//     partial login session AND as step-up on a full session — either way
//     it stamps sessions.totp_verified_at, which is what unlocks /admin/*.
//   * GET  /api/v1/account/2fa/status     → { enrolled, backup_codes_remaining }
//   * POST /api/v1/account/2fa/enroll/start
//         → { secret, otpauth_url, backup_codes } (codes shown ONCE)
//   * POST /api/v1/account/2fa/enroll/confirm { code } → 204
//   * POST /api/v1/account/2fa/disable    { password, code } → 204
//   * POST /api/v1/account/2fa/backup-codes/regenerate { code }
//         → { backup_codes }
//
// Every mutation invalidates ["account-me"] (totp_enrolled /
// session_totp_verified live there) and ["two-factor-status"].

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface TwoFactorError {
  code: string;
  message: string;
  status: number;
}

async function parseError(
  response: Response,
  fallback: string,
): Promise<TwoFactorError> {
  let detail: TwoFactorError = {
    code: "TWO_FACTOR_REQUEST_FAILED",
    message: `${fallback} (HTTP ${response.status})`,
    status: response.status,
  };
  try {
    const raw = await response.json();
    if (raw?.error) {
      detail = {
        code: raw.error.code,
        message: raw.error.message,
        status: response.status,
      };
    }
  } catch {
    // body not JSON — keep default
  }
  return detail;
}

async function postJson<T>(
  url: string,
  body: object,
  fallback: string,
): Promise<T | null> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw await parseError(response, fallback);
  }
  if (response.status === 204) return null;
  return (await response.json()) as T;
}

// ----- status -----

export interface TwoFactorStatus {
  enrolled: boolean;
  backup_codes_remaining: number;
}

export function useTwoFactorStatus() {
  return useQuery<TwoFactorStatus, TwoFactorError>({
    queryKey: ["two-factor-status"],
    queryFn: async () => {
      const response = await fetch("/api/v1/account/2fa/status", {
        credentials: "include",
      });
      attachRequestIdToScope(response);
      if (!response.ok) {
        throw await parseError(response, "2FA status fetch failed");
      }
      return (await response.json()) as TwoFactorStatus;
    },
    retry: false,
  });
}

// ----- verify (login step / admin step-up) -----

export function useTwoFactorVerify() {
  const queryClient = useQueryClient();
  return useMutation<null, TwoFactorError, { code: string }>({
    mutationFn: ({ code }) =>
      postJson("/api/v1/auth/2fa-verify", { code }, "2FA verification failed"),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
      queryClient.invalidateQueries({ queryKey: ["two-factor-status"] });
    },
  });
}

// ----- enrollment -----

export interface EnrollStartResponse {
  secret: string;
  otpauth_url: string;
  backup_codes: string[];
}

export function useTwoFactorEnrollStart() {
  return useMutation<EnrollStartResponse, TwoFactorError, void>({
    mutationFn: () =>
      postJson<EnrollStartResponse>(
        "/api/v1/account/2fa/enroll/start",
        {},
        "2FA enrollment start failed",
      ).then((r) => {
        if (!r) throw new Error("empty enroll/start response");
        return r;
      }),
    retry: false,
  });
}

export function useTwoFactorEnrollConfirm() {
  const queryClient = useQueryClient();
  return useMutation<null, TwoFactorError, { code: string }>({
    mutationFn: ({ code }) =>
      postJson(
        "/api/v1/account/2fa/enroll/confirm",
        { code },
        "2FA enrollment confirm failed",
      ),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
      queryClient.invalidateQueries({ queryKey: ["two-factor-status"] });
    },
  });
}

// ----- management -----

export function useTwoFactorDisable() {
  const queryClient = useQueryClient();
  return useMutation<null, TwoFactorError, { password: string; code: string }>({
    mutationFn: ({ password, code }) =>
      postJson(
        "/api/v1/account/2fa/disable",
        { password, code },
        "2FA disable failed",
      ),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
      queryClient.invalidateQueries({ queryKey: ["two-factor-status"] });
    },
  });
}

export interface RegenerateBackupCodesResponse {
  backup_codes: string[];
}

export function useRegenerateBackupCodes() {
  const queryClient = useQueryClient();
  return useMutation<
    RegenerateBackupCodesResponse,
    TwoFactorError,
    { code: string }
  >({
    mutationFn: ({ code }) =>
      postJson<RegenerateBackupCodesResponse>(
        "/api/v1/account/2fa/backup-codes/regenerate",
        { code },
        "Backup-code regeneration failed",
      ).then((r) => {
        if (!r) throw new Error("empty regenerate response");
        return r;
      }),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["two-factor-status"] });
    },
  });
}
